/**
 * AUTOGENERATED FILE. DO NOT EDIT.
 *
 * This file is generated by running yarn i18n:build, do not edit it manually.
 *
 * If you wish to update it, please run yarn i18n:build.
 */
/* eslint-disable no-restricted-imports */
import {i18n} from "@lingui/core";
import * as React from "react";

import {RequestInfo} from "@repo/shared-core";

// We statically import the English strings as we use them as a fallback
// for locales that don't have any translations. Additionally, it improves
// the performance of the English site by not having to load the strings at
// runtime.
// NOTE(john): This is replaced with the actual English strings in the build
// process.
const enStrings = {"wZQPfd":["Accessibility Statement"],"WN6y56":["Khan Academy Accessibility Statement"],"oIDTlg":["Last Updated: ",["0"]],"aIXuEm":["At Khan Academy accessibility is a direct extension of our mission to provide a free, world-class education to anyone, anywhere. We are committed to making our products available to learners across the wide spectrum of ability."],"UIyKUk":["Accessibility Implementation"],"6wTpzO":["We continue our journey to increase the accessibility of our digital materials and products by collaborating with an external partner and implementing tactical measures to include accessibility throughout our processes and procedures. As a commitment to this work, we have adopted the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA as our baseline to guide us in our development to deliver accessibility materials. We proactively search ways to incorporate accessibility and are committed to continuous improvement. Here are some current activities:"],"9HURiq":["Partnering with accessibility experts on strategy, approach, and continuous improvement"],"4ZZUyc":["Prioritizing critical accessibility fixes to our digital materials"],"cHD88X":["Incorporating access practices into our processes through analysis, tools, and training"],"NNGHli":["Seek User Feedback"],"sMO6JC":["We welcome feedback on the accessibility of Khan Academy. Simply put, our efforts cannot succeed without it. Please let us know if you encounter accessibility barriers or have suggestions for improving the accessibility of our platform."],"pySJq4":["Web: <0>Submit Feedback</0>"],"Dm3qwS":["Email: <0>accessibility@khanacademy.org</0>"]};

let results: Record<string, string | string[]> | undefined = undefined;
let promise: Promise<void> | null = null;

// We keep track of locales that are missing translations so we can load the
// English strings for those locales.
// NOTE(john): This array is replaced with the list of locales.
const missingTranslations: string[] = ["zh-hans","ur","pl","pa","km","zh-hant","uz","ta","sv","sk","sgn-us","rw","ru","pt-pt","my","mn","lv","lt","lol","ky","kk","is","gu","fv","en-x-rtl","el","da","as","zu","xh","th","te","sw","fa","ms","he","fa-af","ar","sq"];

// We keep track of locales that have been bundled together in the same file
// so we can load them all at once.
const extraLocales: string[] = ["es","vi","uk","tr","sr","ro","pt","or","nb","ko","ka","hy","hi","fr","de","bn","bg","nl","mr","kn","ja","it","id","hu","en-pt","cs","boxes","az","accents"];

/**
 * Load the i18n strings for the current locale and add them to the Lingui i18n
 * instance. This uses Suspsense so you'll want to make sure that your
 * component is wrapped in a Suspense (for loading) and an ErrorBoundary
 * (for errors).
 *
 * This function is called by the LoadStrings component, which is a Loadable
 * component that loads the strings for a route and all descendant imports.
 *
 * You can use this directly or you can use the withStrings HoC to load
 * strings for a component.
 */
export const useStrings = () => {
    if (results) {
        return results;
    }

    const locale = RequestInfo.kaLocale;

    // If the locale is English, or doesn't have any translations, we don't need to load
    // any additional strings.
    if (locale === "en" || missingTranslations.includes(locale)) {
        // Load the English strings into Lingui. This serves as a fallback for
        // locales that don't have any translations (and the actual strings for the English
        // locale).
        results = enStrings;
        i18n.load(locale, results);
        return results;
    }

    if (!promise) {
        promise = (async () => {
            const locale = RequestInfo.kaLocale;
            const localeFile = extraLocales.includes(locale) ? "extra" : locale;

            if (process.env.NODE_ENV === "production") {
                const result = (await import(`./lingui/${localeFile}.json`))
                    .default;
                results = localeFile === "extra" ? result[locale] : result;
            } else {
                results = {};
            }

            // Load the strings into Lingui
            i18n.load(locale, {...enStrings, ...results});

            promise = null;
        })();
    }

    throw promise;
};

/**
 * The HOC to load i18n strings for a route and all descendant imports.
 *
 * Examples:
 *
 * - export default withStrings(WrappedComponent);
 *
 * - export default compose(
 *      withStrings,
 *      withRouter
 *   )(WrappedComponent);
 */
export const withStrings = <C extends object>(
    WrappedComponent: React.ComponentType<C>,
) => {
    if (process.env.NODE_ENV === "test") {
        return WrappedComponent;
    }

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    // Creating the inner component.
    const WrapperComponent = (props: C): React.ReactNode => {
        useStrings();
        return <WrappedComponent {...(props as C)} />;
    };

    WrapperComponent.displayName = `withStrings(${displayName})`;

    return WrapperComponent;
};
